import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { abs } from "@/lib/site";

// Bands come from the full symbol index — dynamic-import in the loader so the
// route module stays out of the global (routeTree) bundle. prev/next nav is
// resolved here and returned, so the component needs no heavy static import.
export const Route = createFileRoute("/index/codepoints/$range")({
  loader: async ({ params }) => {
    const { bandIntro, codepointBands, getCodepointBand } = await import("@/data/indexes");
    const band = getCodepointBand(params.range);
    if (!band) throw notFound();
    const idx = codepointBands.findIndex((b) => b.slug === band.slug);
    const prev = idx > 0 ? { slug: codepointBands[idx - 1].slug, label: codepointBands[idx - 1].label } : null;
    const next =
      idx >= 0 && idx < codepointBands.length - 1
        ? { slug: codepointBands[idx + 1].slug, label: codepointBands[idx + 1].label }
        : null;
    return { band, intro: bandIntro(band), prev, next };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { band } = loaderData;
    const title = `${band.label} — ${band.symbols.length} indexed Unicode characters`;
    const description = `Every indexed symbol in the ${band.label} codepoint range. ${band.symbols.length} characters with copy-paste glyphs, names and detail pages.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: abs(`/index/codepoints/${params.range}`) },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: abs(`/index/codepoints/${params.range}`) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            url: `/index/codepoints/${params.range}`,
            numberOfItems: band.symbols.length,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: abs("/") },
              { "@type": "ListItem", position: 2, name: "Codepoint ranges", item: abs("/index/codepoints") },
              { "@type": "ListItem", position: 3, name: band.label, item: abs(`/index/codepoints/${params.range}`) },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <div className="flex flex-1 items-center justify-center p-12 text-center">
        <div>
          <h1 className="font-display text-3xl font-bold">Range not found</h1>
          <Link to="/index/codepoints" className="mt-4 inline-block text-accent hover:underline">
            ← Back to codepoint ranges
          </Link>
        </div>
      </div>
      <SiteFooter />
    </div>
  ),
  component: BandPage,
});

function BandPage() {
  const { band, intro, prev, next } = Route.useLoaderData();

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="mx-auto w-full max-w-7xl px-4 pb-6 pt-8 sm:px-6 sm:pt-16">
          <nav className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:text-sm">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <Link to="/index/codepoints" className="hover:text-foreground">Codepoint ranges</Link>
            <span>/</span>
            <span className="text-foreground">{band.label}</span>
          </nav>

          <h1 className="font-display text-3xl font-bold sm:text-5xl">
            <span className="font-mono">{band.label}</span>
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {band.symbols.length} indexed character{band.symbols.length === 1 ? "" : "s"} · 256-codepoint band
          </p>
          <div className="prose prose-invert mt-5 max-w-3xl space-y-4 text-foreground/90">
            {intro.map((p: string, i: number) => (
              <p key={i} className="text-[15px] leading-relaxed">
                {p}
              </p>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 pb-12 sm:px-6">
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {band.symbols.map((s: typeof band.symbols[number]) => (
              <li key={s.slug}>
                <Link
                  to="/symbol/$slug"
                  params={{ slug: s.slug }}
                  className="glass flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:border-primary/40"
                >
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-xl">
                    {s.symbol}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium">{s.name}</span>
                    <span className="block font-mono text-xs text-muted-foreground">
                      U+{s.primary.toString(16).toUpperCase().padStart(4, "0")}
                    </span>
                  </span>
                  <span className="text-muted-foreground">→</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {(prev || next) && (
          <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
              {prev ? (
                <Link
                  to="/index/codepoints/$range"
                  params={{ range: prev.slug }}
                  className="glass flex-1 rounded-xl p-4 text-sm transition-colors hover:border-primary/40"
                >
                  <span className="block text-xs text-muted-foreground">← Previous band</span>
                  <span className="font-mono">{prev.label}</span>
                </Link>
              ) : <span className="flex-1" />}
              {next ? (
                <Link
                  to="/index/codepoints/$range"
                  params={{ range: next.slug }}
                  className="glass flex-1 rounded-xl p-4 text-right text-sm transition-colors hover:border-primary/40"
                >
                  <span className="block text-xs text-muted-foreground">Next band →</span>
                  <span className="font-mono">{next.label}</span>
                </Link>
              ) : <span className="flex-1" />}
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
